import React from 'react'
import dayjs from 'dayjs'
import {
  Box,
  Stack,
  Divider,
  TextInput,
  Button,
  Group,
  Select,
  Table,
  ActionIcon,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconTrash, IconPlus } from '@tabler/icons-react'
import { DataPoint, useMetrics, useDataKeys, useData } from '../api'
import { useWidgetContext } from '../providers'
import { WidgetWrapper } from '../WidgetWrapper'
import { z } from 'zod'
import { useWidgetEditor } from '../state'
import { COMPONENTS, STATISTIC } from '../schemas'
import { FmtColored } from '../format'
import { PeriodTableOptions } from '../schemas'
import classes from './common.module.css'

type OptionsType = z.infer<typeof PeriodTableOptions>
type ColumnConfig = OptionsType['columns'][number]

export default function PeriodTable(props: { id: string }) {
  const ctx = useWidgetContext()
  const editor = useWidgetEditor()
  const config = editor.useConfig(props.id)
  const { data: metrics } = useMetrics()
  const { data: dataKeys } = useDataKeys()

  // Sorting state
  const [sortConfig, setSortConfig] = React.useState<{
    key: string | null
    direction: 'asc' | 'desc'
  }>({ key: null, direction: 'asc' })

  if (config == null || config.widget !== 'PeriodTable') {
    console.error(
      `Invalid widget config for PeriodTable widget ${props.id}`,
      config
    )
    return null
  }

  const options: OptionsType = config.options
  const columns = (options.columns || []).filter((c) => {
    const metricKey = c.metric || ctx.metric || ''
    const metric = metrics.find((m) => m.key === metricKey)
    if (c.component === 'percent' && metric?.is_percent_metric) {
      return false
    }
    return true
  })

  // Generate months from competition start to end
  const periods = React.useMemo(() => {
    if (!ctx.competition_start || !ctx.competition_end) {
      // Fallback to year-based generation if competition dates not available
      const contextPeriod = ctx.period || dayjs().format('YYYY-MM-DD')
      const year = dayjs(contextPeriod).year()
      return Array.from({ length: 12 }, (_, i) => {
        const month = dayjs(`${year}-01-01`).add(i, 'month')
        return {
          date: month.format('YYYY-MM-DD'),
          label: month.format('MMMM'),
        }
      })
    }

    const startDate = dayjs(ctx.competition_start).startOf('month')
    const endDate = dayjs(ctx.competition_end).endOf('month')
    const periodsArray = []

    let currentDate = startDate
    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, 'month')
    ) {
      periodsArray.push({
        date: currentDate.format('YYYY-MM-DD'),
        label: currentDate.format('MMM'),
      })
      currentDate = currentDate.add(1, 'month')
    }

    return periodsArray
  }, [ctx.competition_start, ctx.competition_end, ctx.period])

  // Extract unique metrics and their configurations from columns
  const queryData = React.useMemo(() => {
    const metricConfigs: {
      metric: string
      component: string
      statistic: string
    }[] = []

    columns.forEach((col) => {
      metricConfigs.push({
        metric: col.metric || ctx.metric || '',
        component: col.component || ctx.component || '',
        statistic: col.statistic || ctx.statistic || '',
      })
    })

    return {
      metrics: metricConfigs.map((c) => c.metric),
      components: metricConfigs.map((c) => c.component),
      statistics: metricConfigs.map((c) => c.statistic),
    }
  }, [columns, ctx.metric, ctx.component, ctx.statistic])

  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    setSortConfig({ key, direction })
  }

  return (
    <WidgetWrapper
      id={props.id}
      transformData={(data: DataPoint[]) => {
        // Create a nested map: period -> metric -> component -> statistic -> value
        const dataMap = new Map<
          string,
          Map<number, Map<number, Map<number, number>>>
        >()

        data.forEach((point) => {
          if (!dataMap.has(point.period)) {
            dataMap.set(point.period, new Map())
          }
          const periodMap = dataMap.get(point.period)!

          if (!periodMap.has(point.metric)) {
            periodMap.set(point.metric, new Map())
          }
          const metricMap = periodMap.get(point.metric)!

          if (!metricMap.has(point.component)) {
            metricMap.set(point.component, new Map())
          }
          const componentMap = metricMap.get(point.component)!

          componentMap.set(point.statistic, point.value)
        })

        const sortedPeriods = [...periods]

        if (sortConfig.key) {
          const nullValue =
            sortConfig.direction === 'asc'
              ? Number.NEGATIVE_INFINITY
              : Number.POSITIVE_INFINITY

          sortedPeriods.sort((a, b) => {
            const sortByColumn = sortConfig.key?.startsWith('col-')
            let aValue: string | number = sortByColumn ? nullValue : a.date
            let bValue: string | number = sortByColumn ? nullValue : b.date

            if (sortByColumn) {
              const colIndex = parseInt(sortConfig.key!.replace('col-', ''))
              const col = columns[colIndex]
              if (col) {
                const metricKey = col.metric || ctx.metric || ''
                const componentKey = col.component || ctx.component || ''
                const statisticKey = col.statistic || ctx.statistic || ''

                const metric = metrics.find((m) => m.key === metricKey)
                if (metric && dataKeys) {
                  const aData = dataMap.get(a.date)
                  const bData = dataMap.get(b.date)

                  if (aData && bData) {
                    const componentId = dataKeys[componentKey]
                    const statisticId = dataKeys[statisticKey]

                    const aMetricData = aData.get(metric.metric_id)
                    const bMetricData = bData.get(metric.metric_id)

                    if (aMetricData && bMetricData) {
                      const aComponentData = aMetricData.get(componentId)
                      const bComponentData = bMetricData.get(componentId)

                      if (aComponentData && bComponentData) {
                        aValue = aComponentData.get(statisticId) ?? nullValue
                        bValue = bComponentData.get(statisticId) ?? nullValue
                      }
                    }
                  }
                }
              }
            }

            if (sortConfig.direction === 'asc') {
              return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
            } else {
              return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
            }
          })
        }

        return { dataMap, sortedPeriods }
      }}
      render={({ dataMap, sortedPeriods }) => (
        <Box className={classes.period_table}>
          <Table highlightOnHover>
            <Table.Thead className={classes.table_head}>
              <Table.Tr>
                <Table.Th
                  style={{
                    fontWeight: 'normal',
                    cursor: 'pointer',
                    userSelect: 'none',
                    width: '100%',
                  }}
                  onClick={() => handleSort('period')}
                >
                  Mth&nbsp;
                  {sortConfig.key === 'period' &&
                    (sortConfig.direction === 'asc' ? '↓' : '↑')}
                </Table.Th>
                {columns.map((col, idx) => (
                  <Table.Th
                    style={{
                      fontWeight: 'normal',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      width: '1%',
                      textAlign: 'right',
                    }}
                    key={idx}
                    onClick={() => handleSort(`col-${idx}`)}
                  >
                    {col.title || col.metric}{' '}
                    {sortConfig.key === `col-${idx}` &&
                      (sortConfig.direction === 'asc' ? '↓' : '↑')}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedPeriods.map((period) => {
                const periodData = dataMap.get(period.date)

                return (
                  <Table.Tr key={period.date}>
                    <Table.Td style={{ width: '100%' }}>
                      {period.label}
                    </Table.Td>
                    {columns.map((col, idx) => {
                      // Use context values as fallback
                      const metricKey = col.metric || ctx.metric || ''
                      const componentKey = col.component || ctx.component || ''
                      const statisticKey = col.statistic || ctx.statistic || ''

                      // Find the metric ID for this column
                      const metric = metrics.find((m) => m.key === metricKey)
                      if (!metric || !periodData || !dataKeys) {
                        return (
                          <Table.Td key={idx} style={{ textAlign: 'center' }}>
                            -
                          </Table.Td>
                        )
                      }

                      const metricData = periodData.get(metric.metric_id)
                      if (!metricData) {
                        return (
                          <Table.Td key={idx} style={{ textAlign: 'center' }}>
                            -
                          </Table.Td>
                        )
                      }

                      // Get component and statistic IDs from the data keys
                      const componentId = dataKeys[componentKey]
                      const statisticId = dataKeys[statisticKey]

                      const componentData = metricData.get(componentId)
                      if (!componentData) {
                        return (
                          <Table.Td key={idx} style={{ textAlign: 'center' }}>
                            -
                          </Table.Td>
                        )
                      }

                      const value = componentData.get(statisticId) ?? null

                      return (
                        <Table.Td
                          key={idx}
                          style={{
                            whiteSpace: 'nowrap',
                            width: '1%',
                            textAlign: value == null ? 'center' : 'right',
                          }}
                        >
                          <FmtColored
                            value={value}
                            metric={metric.metric_id}
                            component={componentKey}
                            statistic={statisticKey}
                          />
                        </Table.Td>
                      )
                    })}
                  </Table.Tr>
                )
              })}
              <TotalsRow columns={columns} />
            </Table.Tbody>
          </Table>
        </Box>
      )}
      query={() => ({
        period_type: 'month',
        period_range: false,
        periods: periods.map((p) => p.date),
        subject_type: ctx.subject_type || 'user',
        subject_ids: [ctx.subject || 0],
        ...queryData,
        competition_id: ctx.competition_id,
      })}
    />
  )
}

function TotalsRow(props: { columns: OptionsType['columns'] }) {
  const ctx = useWidgetContext()
  const mapToYtd = (statistic: string) => {
    return (
      {
        value: 'value_ytd',
        rank: 'rank_ytd',
        team_rank: 'team_rank_ytd',
        achieved: 'achieved_ytd',
        achieved_teams: 'achieved_teams_ytd',
      }[statistic] || ''
    )
  }

  const columns = props.columns.map((col) => ({
    metric: col.metric || ctx.metric || '',
    component: col.component || ctx.component || '',
    statistic: col.statistic || ctx.statistic || '',
    ytdStatistic: mapToYtd(col.statistic || ctx.statistic || ''),
  }))

  const { data } = useData({
    period_type: 'total',
    period_range: false,
    periods: ['2025-01-01'],
    subject_type: ctx.subject_type || 'user',
    subject_ids: [ctx.subject || 0],
    metrics: columns.map((c) => c.metric),
    components: columns.map((c) => c.component),
    statistics: columns.map((c) => c.ytdStatistic),
  })

  return (
    <Table.Tr className={classes.totalsRow}>
      <Table.Td>YTD</Table.Td>
      {columns.map((col, idx) => {
        const value = data.find(
          ({ metric, component, statistic }) =>
            metric?.key == col.metric &&
            component == col.component &&
            statistic == col.ytdStatistic
        )

        return (
          <Table.Td
            key={idx}
            style={{
              whiteSpace: 'nowrap',
              width: '1%',
              textAlign: value == null ? 'center' : 'right',
            }}
          >
            <FmtColored
              metric={value?.metric?.key || ''}
              component={value?.component || ''}
              statistic={value?.statistic || ''}
              value={value?.value}
            />
          </Table.Td>
        )
      })}
    </Table.Tr>
  )
}

// Form component for PeriodTable options
export function PeriodTableForm({ id }: { id: string }) {
  const editor = useWidgetEditor()
  const isSaving = editor.useIsSaving()
  const config = editor.useConfig(id)
  const { data: metrics } = useMetrics()

  if (config.widget !== 'PeriodTable')
    throw new Error('Invalid widget type, expected PeriodTable')

  const form = useForm({
    initialValues: {
      columns: (config.options.columns || []).map((col: ColumnConfig) => ({
        ...col,
      })),
    },
  })

  const handleSubmit = (values: typeof config.options) => {
    editor.setOptions(values)
    editor.save()
  }

  const metricOptions = [
    { value: '', label: '(Use context)' },
    ...metrics.map((metric) => ({
      value: metric.key,
      label: metric.name,
    })),
  ]

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm">
        <Divider label="Columns" />

        {form.values.columns.map((_item: ColumnConfig, index: number) => (
          <Box
            key={index}
            p="sm"
            style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}
          >
            <Group align="flex-start">
              <Stack style={{ flex: 1 }}>
                <Select
                  label="Metric"
                  placeholder="Select a metric"
                  data={metricOptions}
                  searchable
                  {...form.getInputProps(`columns.${index}.metric`)}
                />

                <Group grow>
                  <Select
                    label="Component"
                    placeholder="Select component"
                    data={[
                      { value: '', label: '(Use context)' },
                      ...COMPONENTS.slice(1),
                    ]}
                    {...form.getInputProps(`columns.${index}.component`)}
                  />

                  <Select
                    label="Statistic"
                    placeholder="Select statistic"
                    data={[
                      { value: '', label: '(Use context)' },
                      ...STATISTIC.slice(1),
                    ]}
                    {...form.getInputProps(`columns.${index}.statistic`)}
                  />
                </Group>

                <TextInput
                  label="Column Title"
                  placeholder="Custom column title"
                  {...form.getInputProps(`columns.${index}.title`)}
                />
              </Stack>

              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => form.removeListItem('columns', index)}
                style={{ marginTop: '24px' }}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Box>
        ))}

        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={() =>
            form.insertListItem('columns', {
              metric: '',
              component: '',
              statistic: '',
              title: '',
            })
          }
        >
          Add Column
        </Button>
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button color="red.4" variant="outline" onClick={editor.endEditWidget}>
          Cancel
        </Button>
        <Button type="submit" loading={isSaving}>
          Save
        </Button>
      </Group>
    </form>
  )
}

export const PeriodTableRegistry = {
  component: PeriodTable,
  formComponent: PeriodTableForm,
  mock: () => (
    <Box p="md" style={{ backgroundColor: 'white', borderRadius: '8px' }}>
      <h3 style={{ margin: '0 0 16px 0' }}>Period Table</h3>
      <Table highlightOnHover>
        <Table.Thead style={{ backgroundColor: '#DEE2E6' }}>
          <Table.Tr>
            <Table.Th style={{ fontWeight: 'normal', width: '100%' }}>
              Period
            </Table.Th>
            <Table.Th
              style={{ fontWeight: 'normal', width: '1%', textAlign: 'right' }}
            >
              Sales Actual
            </Table.Th>
            <Table.Th
              style={{ fontWeight: 'normal', width: '1%', textAlign: 'right' }}
            >
              Sales Target
            </Table.Th>
            <Table.Th
              style={{ fontWeight: 'normal', width: '1%', textAlign: 'right' }}
            >
              Achievement %
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td style={{ width: '100%' }}>Jan 2025</Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              85,420
            </Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              90,000
            </Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              94.9%
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td style={{ width: '100%' }}>Feb 2025</Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              92,150
            </Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              90,000
            </Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              102.4%
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td style={{ width: '100%' }}>Mar 2025</Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              88,900
            </Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              95,000
            </Table.Td>
            <Table.Td style={{ width: '1%', textAlign: 'right' }}>
              93.6%
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Box>
  ),
  getInitialOptions: () => PeriodTableOptions.parse({}),
}
